import { useId } from "react";
import { cn } from "@/lib/utils";
import { isClientHost } from "@/lib/clientHost";

// MyNextBigMove.com mark: an ascending "M" whose last stroke breaks out as an arrow.
export const BrandMark = ({ className = "h-9 w-9", glow = true }) => (
  <svg viewBox="0 0 64 64" className={cn("shrink-0", glow && "drop-shadow-[0_0_14px_rgba(130,36,227,0.75)]", className)} aria-hidden="true">
    <defs>
      <linearGradient id="nbm-gold" x1="0" y1="1" x2="1" y2="0"><stop offset="0" stopColor="#A8831F" /><stop offset="0.45" stopColor="#D4AF37" /><stop offset="0.75" stopColor="#F7DC7A" /><stop offset="1" stopColor="#D4AF37" /></linearGradient>
      <linearGradient id="nbm-tile" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#1A1024" /><stop offset="1" stopColor="#09090B" /></linearGradient>
      <radialGradient id="nbm-halo" cx="0.7" cy="0.25" r="0.75"><stop offset="0" stopColor="#8224e3" stopOpacity="0.55" /><stop offset="1" stopColor="#8224e3" stopOpacity="0" /></radialGradient>
    </defs>
    <rect x="2" y="2" width="60" height="60" rx="16" fill="url(#nbm-tile)" />
    <rect x="2" y="2" width="60" height="60" rx="16" fill="url(#nbm-halo)" />
    <rect x="2.75" y="2.75" width="58.5" height="58.5" rx="15.25" fill="none" stroke="url(#nbm-gold)" strokeOpacity="0.55" strokeWidth="1.5" />
    <path d="M14 47 V24 L27 37 L37 27 L47 17" fill="none" stroke="url(#nbm-gold)" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M36.5 16.5 H47.5 V27.5" fill="none" stroke="url(#nbm-gold)" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M37 37 V47" stroke="#B57BFF" strokeWidth="5.5" strokeLinecap="round" />
    <circle cx="47.5" cy="16.5" r="2.2" fill="#FFF6D6" />
  </svg>
);

// MyNextBigMove.co mark: the rising "M" inside radar rings with a live signal ping (find demand, then move on it).
export const ProMark = ({ className = "h-9 w-9", glow = true }) => {
  const u = useId().replace(/:/g, "");
  return (
  <svg viewBox="0 0 64 64" className={cn("shrink-0", glow && "drop-shadow-[0_0_16px_rgba(212,175,55,0.45)]", className)} aria-hidden="true">
    <defs>
      <linearGradient id={`nbc-gold-${u}`} x1="0" y1="1" x2="1" y2="0"><stop offset="0" stopColor="#A8831F" /><stop offset="0.5" stopColor="#E9C65A" /><stop offset="1" stopColor="#FFF0B8" /></linearGradient>
      <radialGradient id={`nbc-core-${u}`} cx="0.5" cy="0.5" r="0.5"><stop offset="0" stopColor="#24133A" /><stop offset="1" stopColor="#09090B" /></radialGradient>
      <linearGradient id={`nbc-sweep-${u}`} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#B57BFF" stopOpacity="0.55" /><stop offset="1" stopColor="#B57BFF" stopOpacity="0" /></linearGradient>
    </defs>
    <circle cx="32" cy="32" r="30" fill={`url(#nbc-core-${u})`} />
    <circle cx="32" cy="32" r="29.2" fill="none" stroke={`url(#nbc-gold-${u})`} strokeWidth="1.6" />
    <circle cx="32" cy="32" r="21" fill="none" stroke="#D4AF37" strokeOpacity="0.22" strokeWidth="1" />
    <circle cx="32" cy="32" r="12.5" fill="none" stroke="#D4AF37" strokeOpacity="0.16" strokeWidth="1" />
    <path d="M32 32 L32 3 A29 29 0 0 1 56.5 17 Z" fill={`url(#nbc-sweep-${u})`} />
    <path d="M17 44 V25 L27 35 L35 27 L45 18" fill="none" stroke={`url(#nbc-gold-${u})`} strokeWidth="4.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M36 17.5 H45.5 V27" fill="none" stroke={`url(#nbc-gold-${u})`} strokeWidth="4.6" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="45.5" cy="17.5" r="5.5" fill="#B57BFF" fillOpacity="0.25" />
    <circle cx="45.5" cy="17.5" r="2.3" fill="#FFF6D6" />
  </svg>
  );
};

const SIZES = { sm: ["h-7 w-7", "text-[15px]", "text-[7.5px]", "text-[10px]"], md: ["h-9 w-9", "text-lg", "text-[8.5px]", "text-[11px]"], lg: ["h-12 w-12", "text-2xl", "text-[10px]", "text-[13px]"] };

// Recruiter/sales product lockup for mynextbigmove.co.
export const ProBrandLogo = ({ size = "md", tagline = true, className, testid }) => {
  const s = SIZES[size];
  return (
    <span className={cn("inline-flex select-none items-center gap-2.5", className)} aria-label="MyNextBigMove.co for Recruiters & Sales Professionals, powered by RPOPRO.co" data-testid={testid}>
      <ProMark className={s[0]} />
      <span className="flex flex-col leading-none">
        <span className={cn("whitespace-nowrap font-display font-bold tracking-[-0.02em]", s[1])}>
          <span className="text-white">MyNext</span><span className="bg-gradient-to-r from-[#D4AF37] via-[#F7DC7A] to-[#D4AF37] bg-clip-text text-transparent">BigMove</span><span className="text-[#B57BFF] [text-shadow:0_0_12px_rgba(130,36,227,0.8)]">.co</span>
        </span>
        {tagline && <>
          <span className={cn("mt-1 whitespace-nowrap font-medium text-zinc-300", s[3])} data-testid={testid && `${testid}-for`}>for Recruiters <span className="text-[#D4AF37]">&amp;</span> Sales Professionals</span>
          <span className={cn("mt-1 whitespace-nowrap font-mono font-semibold uppercase tracking-[0.28em] text-zinc-500", s[2])}>powered by <span className="text-[#D4AF37]">RPOPRO.co</span></span>
        </>}
      </span>
    </span>
  );
};

// Full lockup. size: sm | md | lg. tagline adds "powered by RPOPRO.co". On mynextbigmove.co the recruiter lockup is used.
export const BrandLogo = ({ size = "md", tagline = true, className, testid, pro = isClientHost() }) => {
  if (pro) return <ProBrandLogo size={size} tagline={tagline} className={className} testid={testid} />;
  const s = SIZES[size];
  return (
    <span className={cn("inline-flex select-none items-center gap-2.5", className)} aria-label="MyNextBigMove.com, powered by RPOPRO.co" data-testid={testid}>
      <BrandMark className={s[0]} />
      <span className="flex flex-col leading-none">
        <span className={cn("whitespace-nowrap font-display font-bold tracking-[-0.02em]", s[1])}>
          <span className="text-white">MyNext</span>
          <span className="bg-gradient-to-r from-[#D4AF37] via-[#F7DC7A] to-[#D4AF37] bg-clip-text text-transparent">BigMove</span>
          <span className="text-[#B57BFF] [text-shadow:0_0_12px_rgba(130,36,227,0.8)]">.com</span>
        </span>
        {tagline && <span className={cn("mt-1 whitespace-nowrap font-mono font-semibold uppercase tracking-[0.28em] text-zinc-500", s[2])}>powered by <span className="text-[#D4AF37]">RPOPRO.co</span></span>}
      </span>
    </span>
  );
};
